import { useEffect, useRef } from "react";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";
import { fetcher } from "@/lib/api";
import type {
  CoreNotification,
  CoreNotificationResponse,
  CoreUnreadCountResponse,
} from "../types/core-notification.types";

const PAGE_LIMIT = 6;

function useIsFocused() {
  const isFocusedRef = useRef(true);

  useEffect(() => {
    isFocusedRef.current = document.visibilityState === "visible";
    const handler = () => {
      isFocusedRef.current = document.visibilityState === "visible";
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  return isFocusedRef;
}

export function useCoreNotifications(enabled: boolean = true) {
  const isFocusedRef = useIsFocused();

  const getKey = (
    pageIndex: number,
    previousPageData: CoreNotificationResponse | null,
  ) => {
    if (!enabled) return null;
    if (previousPageData && pageIndex >= previousPageData.totalPages) return null;
    return `/core-notifications?page=${pageIndex + 1}&limit=${PAGE_LIMIT}&sortBy=createdAt&sortOrder=desc`;
  };

  const { data, size, setSize, isLoading, error, mutate } =
    useSWRInfinite<CoreNotificationResponse>(
      getKey,
      fetcher,
      {
        revalidateOnFocus: true,
        revalidateOnReconnect: true,
        refreshInterval: (latestData: CoreNotificationResponse[] | undefined) => {
          if (!latestData) return 0;
          const all = latestData.flatMap((page) => page?.data ?? []);
          const hasLoading = all.some((n) => n.type === "loading");
          if (hasLoading) return 3000;
          if (!isFocusedRef.current) return 0;
          return 60000;
        },
        shouldRetryOnError: (err: unknown) => {
          if (err && typeof err === "object" && "status" in err) {
            return (err as { status: number }).status !== 429;
          }
          return false;
        },
        errorRetryCount: 1,
        errorRetryInterval: 10000,
        keepPreviousData: true,
      },
    );

  const notifications: CoreNotification[] =
    data?.flatMap((page) => page?.data ?? []) ?? [];

  const totalPages = data?.[0]?.totalPages ?? 0;
  const hasMore = size < totalPages;

  return {
    notifications,
    total: data?.[0]?.total ?? 0,
    isLoading,
    isError: error,
    mutate,
    hasMore,
    loadMore: () => {
      if (hasMore) setSize((s) => s + 1);
    },
  };
}

export function useCoreUnreadNotificationsCount(enabled: boolean = true) {
  const isFocusedRef = useIsFocused();

  const { data, error, isLoading, mutate } = useSWR<CoreUnreadCountResponse>(
    enabled ? "/core-notifications/unread-count" : null,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: (latestData: CoreUnreadCountResponse | undefined) => {
        if (!latestData) return 0;
        if (!isFocusedRef.current) return 0;
        return 60000;
      },
      shouldRetryOnError: (err: unknown) => {
        if (err && typeof err === "object" && "status" in err) {
          return (err as { status: number }).status !== 429;
        }
        return false;
      },
      errorRetryCount: 1,
      errorRetryInterval: 10000,
      keepPreviousData: true,
    },
  );

  return {
    count: data?.count ?? 0,
    isLoading,
    isError: error,
    mutate,
  };
}
